import { useState } from "react";

type Page =
  | "dashboard"
  | "profile"
  | "budget"
  | "portfolio"
  | "property-detail"
  | "loans"
  | "transfers"
  | "simulator"
  | "strategy"
  | "reports"
  | "tax"
  | "billing"
  | "settings"
  | "admin";

const PRIMARY_TABS: { id: Page; label: string; icon: React.ReactNode }[] = [
  { id: "dashboard", label: "Dashboard", icon: <HomeIcon /> },
  { id: "portfolio", label: "Portfolio", icon: <PortfolioIcon /> },
  { id: "strategy", label: "Strategy", icon: <StrategyIcon /> },
  { id: "simulator", label: "Simulator", icon: <SimIcon /> },
];

const MORE_ITEMS: { id: Page; label: string }[] = [
  { id: "profile", label: "My Profile" },
  { id: "budget", label: "Personal Budget" },
  { id: "loans", label: "Loans" },
  { id: "transfers", label: "Cashflow & Transfers" },
  { id: "reports", label: "Reports" },
  { id: "tax", label: "Tax" },
  { id: "billing", label: "Billing" },
  { id: "settings", label: "Settings" },
];

export default function BottomNav({
  current,
  setCurrent,
  hasPortfolio = true,
  hasGoals = true,
}: {
  current: Page;
  setCurrent: (p: Page) => void;
  hasPortfolio?: boolean;
  hasGoals?: boolean;
}) {
  const [moreOpen, setMoreOpen] = useState(false);

  const isMoreActive = MORE_ITEMS.some((i) => i.id === current);

  function navigate(page: Page) {
    setCurrent(page);
    setMoreOpen(false);
  }

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* Slide-up sheet */}
      {moreOpen && (
        <div className="fixed bottom-16 left-0 right-0 z-50 md:hidden bg-white rounded-t-2xl shadow-xl p-4">
          <div className="w-12 h-1 rounded-full bg-neutral-200 mx-auto mb-4" />
          <div className="space-y-1">
            {MORE_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.id)}
                className={`w-full text-left px-4 py-3 rounded-xl transition ${
                  current === item.id
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-neutral-200 flex">
        {PRIMARY_TABS.map((tab) => {
          const showDot =
            (tab.id === "portfolio" && !hasPortfolio) ||
            (tab.id === "strategy" && !hasGoals);
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.id)}
              className={`flex-1 flex flex-col items-center justify-center py-2 gap-1 text-xs transition-colors ${
                current === tab.id ? "text-neutral-900" : "text-neutral-400"
              }`}
            >
              <span className="relative">
                {tab.icon}
                {showDot && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#C47A2F]" />
                )}
              </span>
              <span>{tab.label}</span>
            </button>
          );
        })}

        {/* More tab */}
        <button
          onClick={() => setMoreOpen((v) => !v)}
          className={`flex-1 flex flex-col items-center justify-center py-2 gap-1 text-xs transition-colors ${
            isMoreActive || moreOpen ? "text-neutral-900" : "text-neutral-400"
          }`}
        >
          <MoreIcon />
          <span>More</span>
        </button>
      </div>
    </>
  );
}

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

function HomeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function PortfolioIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <line x1="12" y1="12" x2="12" y2="16" />
      <line x1="10" y1="14" x2="14" y2="14" />
    </svg>
  );
}

function StrategyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function SimIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
